import { Loader2 } from 'lucide-react';
import React from 'react';

import type { QcQuickFilter } from '@/pages/qc/utils';
import { cn } from '@/utils';

interface QcStatCounts {
  all: number;
  needsReview: number;
  escalations: number;
  complianceFailure: number;
}

interface QcStatsTabsProps {
  activeFilter: QcQuickFilter;
  counts: QcStatCounts;
  isCountsLoading?: boolean;
  onChange: (value: QcQuickFilter) => void;
}

const tabs: Array<{
  value: QcQuickFilter;
  label: string;
  getCount: (counts: QcStatCounts) => number;
  badgeColor: string;
}> = [
  {
    value: 'all',
    label: 'All',
    getCount: counts => counts.all,
    badgeColor: 'bg-secondary text-secondary-foreground',
  },
  {
    value: 'needs_review',
    label: 'Needs Review',
    getCount: counts => counts.needsReview,
    badgeColor: 'bg-attention text-attention-foreground',
  },
  {
    value: 'escalations',
    label: 'Escalations',
    getCount: counts => counts.escalations,
    badgeColor: 'bg-attention text-attention-foreground',
  },
  {
    value: 'compliance_failure',
    label: 'Compliance Failure',
    getCount: counts => counts.complianceFailure,
    badgeColor: 'bg-notice-muted text-notice-foreground',
  },
];

export const QcStatsTabs: React.FC<QcStatsTabsProps> = ({
  activeFilter,
  counts,
  isCountsLoading = false,
  onChange,
}) => {
  return (
    <div className="inline-flex flex-wrap items-center gap-1 rounded-lg bg-muted/60 p-1">
      {tabs.map(tab => {
        const isActive = activeFilter === tab.value;
        return (
          <button
            key={tab.value}
            type="button"
            onClick={() => onChange(tab.value)}
            className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              isActive
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:bg-background/70'
            }`}
          >
            <span>{tab.label}</span>
            <span
              className={cn(
                'inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-xs font-semibold rounded-md',
                isActive ? tab.badgeColor : 'bg-muted text-muted-foreground',
              )}
            >
              {isCountsLoading ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                tab.getCount(counts)
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
};
